import {CartItem, TotalPriceItemCart} from "@eshop/types/cart.types.ts";
import React, {useEffect, useState} from "react";
import {RootState, useAppDispatch} from "@store/store.tsx";
import {useDebounce} from "use-debounce";
import {applyVoucherAsync, removeVoucherAsync} from "@store/slices/cartSlice.tsx";
import {Alert, Button, Input, Popconfirm, Space, Table, Tag} from "antd";
import {CheckOutlined, DeleteOutlined, MinusOutlined, PlusOutlined} from "@ant-design/icons";
import {getFormattedPrice} from "@tools/utils/common.tsx";
import {updateCartItemQuantity} from "@eshop/components/cart/cartUtils.tsx";
import {useSelector} from "react-redux";
import { attemptCartLineRemoval } from "@eshop/pages/catalogue/CatalogueUtils.tsx";
import { getPotentialPaymentPromotionMessages } from "@eshop/utils/potentialPaymentPromotions.ts";


interface ColumnsOptions {
    key: string,
    title: string,
    dataIndex: string,
    className: string,
}
interface ChartItemDescriptionProps {
    item: CartItem;
    columnsOptions: ColumnsOptions[];
    setOpenPopConfirmItemDelete: React.Dispatch<React.SetStateAction<number | null>>;
    openPopConfirmItemDelete: number | null;
    currency: string | undefined;
    className?: string;
}
export const ChartItemDescription: React.FC<ChartItemDescriptionProps> = ({
                                                                       item,
                                                                       columnsOptions,
                                                                       setOpenPopConfirmItemDelete,
                                                                       openPopConfirmItemDelete,
                                                                       currency

                                                                   }) => {
    const dispatch = useAppDispatch();
    const [quantity, setQuantity] = useState<number>(item.quantity);
    const [errorMsg, setErrorMsg] = useState<string|undefined>();
    const [debouncedQuantity] = useDebounce(quantity, 500);
    const optionRows = (item.options ?? item.optionsInfo ?? []).map((option, index) => ({
        ...option,
        name: option.name ?? option.code,
        verbose: option.verbose ?? option.value,
        __rowKey: option.code || `${item.id}-${index}`,
    }));

    // Efecto para actualizar el carrito cuando el valor de la cantidad debounced cambia
    useEffect(() => {
        updateCartItemQuantity(
            item,
            debouncedQuantity,
            dispatch,
            setQuantity,
            setErrorMsg
        );
    }, [debouncedQuantity]);   // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setQuantity(item.quantity);
    }, [item.quantity]);

    const handleDecrement = () => {
        if (quantity <= 1) {
            handleRemove();
            return;
        }

        setQuantity(quantity - 1);
    };

    const handleIncrement = () => {
        setQuantity(quantity + 1);
    };

    const handleRemove = () => {
        void (async () => {
            const result = await attemptCartLineRemoval({ cartItem: item, dispatch });
            if (!result.success) {
                setQuantity(result.previousQuantity);
                setErrorMsg(result.errorMessage);
            } else {
                setErrorMsg(undefined);
            }
        })();
    };

    const shouldShowDelete = quantity <= 1;

    return (
        <div>
            {optionRows.length > 0 &&
                <Table
                    columns={columnsOptions}
                    dataSource={optionRows}
                    rowKey={(record) => record.__rowKey}
                    size="small"
                    showHeader={false}
                    pagination={false}/>}
            <div className="d-flex justify-content-between">
                <Popconfirm title={errorMsg} open={!!errorMsg} showCancel={false} onConfirm={() => setErrorMsg(undefined)}>
                    <div className="editQtyBtnsClass d-flex justify-content-center">
                        <Button
                            icon={shouldShowDelete ? <DeleteOutlined /> : <MinusOutlined />}
                            onClick={handleDecrement}
                            danger={shouldShowDelete}
                            aria-label={shouldShowDelete ? "Eliminar" : "Disminuir"}
                        />
                        <p className="px-2 my-auto mx-0">{quantity}</p>
                        <Button icon={<PlusOutlined />} onClick={handleIncrement} />
                    </div>
                </Popconfirm>
                <div className="text-end">
                    <div className="text_web_color_oscuro_3 fw-bold">
                        {getFormattedPrice(item.subtotal.totalSinDesc, currency)}
                    </div>
                    {item.subtotal.descuento ? (
                        <div className="text-danger">
                            -{getFormattedPrice(item.subtotal.descuento, currency)}
                            {item.discount_labels?.map((l) => (
                                <Tag key={l} color="red" className="ms-1 text-wrap">{l}</Tag>
                            ))}
                        </div>
                    ) : null}
                    {item.subtotal.descuento ? (
                        <div className="text_web_color_oscuro_3 fw-bold">
                            {getFormattedPrice(item.subtotal.total, currency)}
                        </div>
                    ) : null}
                </div>
            </div>
            <div className="d-flex justify-content-end">
                <Popconfirm
                    title="¿Estás seguro de que deseas quitar este item?"
                    onConfirm={() => {
                        setOpenPopConfirmItemDelete(null);
                        handleRemove();
                    }}
                    onCancel={() => setOpenPopConfirmItemDelete(null)}
                    okText="Sí"
                    cancelText="No"
                    open={openPopConfirmItemDelete === item.id}
                >
                    <Button
                        shape="circle"
                        icon={<DeleteOutlined/>}
                        onClick={() => setOpenPopConfirmItemDelete(item.id)}
                        className="removeFromCartClass"
                    />
                </Popconfirm>
            </div>
        </div>
    );
};
interface ChartItemDescriptionViewProps {
    item: CartItem;
    columnsOptions: ColumnsOptions[];
    currency: string | undefined;
    className?: string;
}
export const ChartItemDescriptionView: React.FC<ChartItemDescriptionViewProps> = ({
                                                                       item,
                                                                       columnsOptions,
                                                                       currency
                                                                   }) => {
    const optionRows = (item.options ?? item.optionsInfo ?? []).map((option, index) => ({
        ...option,
        name: option.name ?? option.code,
        verbose: option.verbose ?? option.value,
        __rowKey: option.code || `${item.id}-${index}`,
    }));

    return (
      <div>
        {optionRows.length > 0 && (
          <Table
            columns={columnsOptions}
            dataSource={optionRows}
            rowKey={(record) => record.__rowKey}
            size="small"
            showHeader={false}
            pagination={false}
          />
        )}
        <div className="d-flex justify-content-end">
          <div className="text-end">
            <div className="text_web_color_oscuro_3 fw-bold">
              {getFormattedPrice(item.subtotal.totalSinDesc, currency)}
            </div>
            {item.subtotal.descuento ? (
              <div className="text-danger">
                -{getFormattedPrice(item.subtotal.descuento, currency)}
                {item.discount_labels?.map((l) => (
                  <Tag key={l} color="red" className="ms-1 text-wrap">
                    {l}
                  </Tag>
                ))}
              </div>
            ) : null}
            {item.subtotal.descuento ? (
              <div className="text_web_color_oscuro_3 fw-bold">
                {getFormattedPrice(item.subtotal.total, currency)}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
};



interface CartTotalsProps {
    total: TotalPriceItemCart;
    currency?: string;
    className?: string;
    showBreakdown?: boolean;
}

export const CartTotals: React.FC<CartTotalsProps> = ({
    total,
    currency,
    className = 'totalsCartSectionClass w-100 d-flex flex-column',
    showBreakdown = false,
}) => {
    const cart = useSelector((state: RootState) => state.cart);
    const potentialPaymentPromotions = getPotentialPaymentPromotionMessages(cart.cartItems);
    return (
        <div className={className}>
            {potentialPaymentPromotions.slice(0, 2).map((promotion) => (
                <Alert
                    key={promotion.key}
                    type="info"
                    showIcon
                    message={promotion.text}
                    className="mb-2 text-start"
                    style={{ fontSize: 12, padding: '6px 10px' }}
                />
            ))}
            {showBreakdown && (
                <div className="d-flex w-100 justify-content-end mb-2">
                    <div>Subtotal:</div>
                    <div className="ps-2 pl-2">{getFormattedPrice(total.totalSinDesc, currency)}</div>
                </div>
            )}
            {total.descuento ? (
                <div className="d-flex w-100 justify-content-end mb-2">
                    <div>Descuento:</div>
                    <div className="text-danger ps-2 pl-2">-{getFormattedPrice(total.descuento, currency)}</div>
                </div>
            ) : null}
            <h5 className="d-flex w-100 justify-content-end">Total: {getFormattedPrice(total.total, currency)}</h5>
        </div>
    );
};


export const VoucherSection: React.FC = () => {
    const [voucherCode, setVoucherCode] = useState<string>('');
    const cart = useSelector((state: RootState) => state.cart);
    const dispatch = useAppDispatch();

    const handleApplyVoucher = () => {
        if (!voucherCode) {
            alert('Por favor, ingresa un código de descuento.');
            return;
        }
        dispatch(applyVoucherAsync(voucherCode));
        setVoucherCode(''); // Limpiar el campo después de aplicar el voucher
    };

    const handleRemoveVoucher = (code: string) => {
        dispatch(removeVoucherAsync(code));
    };

    return (
        <div className="voucherClass voucher-section mb-2">
            <Space.Compact className="mb-2 w-100">
                <Input placeholder="Ingresa tu código de descuento"
                       value={voucherCode}
                       onChange={(e) => setVoucherCode(e.target.value)}
                       onPressEnter={handleApplyVoucher} />
                <Button onClick={handleApplyVoucher}
                        className={`m-0 p-0 border-0 ${voucherCode && "bg_verde_AV"}`}
                        disabled={!voucherCode}
                        icon={<CheckOutlined />} />
            </Space.Compact>

            {cart.vouchers && cart.vouchers.length > 0 && (
                <>
                    <p className="m-0 font-weight-bold me-2 mr-2 text_gris_AV mb-1">Cupones de descuento:</p>
                    <div className="d-flex">
                        {cart.vouchers.map((v) => (
                            <Tag
                                variant={'outlined'}
                                color="magenta"
                                className="me-2 mr-2 my-2"
                                key={v}
                                closable
                                onClose={() => handleRemoveVoucher(v)}
                            >
                                {v}
                            </Tag>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};
